import React, { useState } from 'react';
import { Sparkles, X, Image as ImageIcon, Camera, Plus, Wand2, CheckCircle2 } from 'lucide-react';
interface ImageGenerationUpsellProps {
  discreet?: boolean;
  inline?: boolean;
  modal?: boolean;
  onClose?: () => void;
  onGenerate?: (options: any) => void;
}
export const ImageGenerationUpsell: React.FC<ImageGenerationUpsellProps> = ({
  discreet = false,
  inline = false,
  modal = false,
  onClose,
  onGenerate
}) => {
  const [selectedCount, setSelectedCount] = useState<number>(4);
  const [showFullGenerator, setShowFullGenerator] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('Pose');
  const [selectedSuggestion, setSelectedSuggestion] = useState<string | null>('Squatting');
  const countOptions = [1, 4, 9, 16];
  const suggestionTabs = ['Outfit', 'Action', 'Pose', 'Accessories', 'Scene'];
  const poseSuggestions = [{
    id: 'Standing',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/t3goBVoY1kATuuDMKdLWrF/image.png',
    label: 'Standing'
  }, {
    id: 'Sitting',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/prw8gBHG3tXNjWoEyVN56M/image.png',
    label: 'Sitting'
  }, {
    id: 'Squatting',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/h6a7zyWq3JC9n2wWu9pSrh/image.png',
    label: 'Squatting'
  }, {
    id: 'Stretching',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/vssdqB5QMf9DhzUe4Cx71y/image.png',
    label: 'Stretching'
  }, {
    id: 'Kneeling',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8vkAZziFM1LtNR5CqsEApr/image.png',
    label: 'Kneeling'
  }];
  const handleGenerate = () => {
    const options = {
      count: selectedCount,
      pose: selectedSuggestion
    };
    onGenerate?.(options);
    if (onClose) onClose();
  };
  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if the click was directly on the backdrop, not on the modal content
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };
  const handleSuggestionSelect = (suggestionId: string) => {
    setSelectedSuggestion(suggestionId);
  };
  // For the discreet mini-banner version
  if (discreet) {
    return <div className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-of-blue dark:hover:border-of-blue transition-all cursor-pointer shadow-sm" onClick={() => setShowFullGenerator(true)} data-id="element-8640">
        <div className="flex items-center gap-2" data-id="element-8641">
          <div className="w-8 h-8 flex items-center justify-center bg-of-blue/10 dark:bg-of-blue/20 rounded-full text-of-blue" data-id="element-8642">
            <ImageIcon size={16} data-id="element-8643" />
          </div>
          <div className="flex-1" data-id="element-8644">
            <p className="text-xs text-gray-700 dark:text-gray-200" data-id="element-8645">
              <span className="text-of-blue" data-id="element-8646">New!</span> Generate custom images
            </p>
            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400" data-id="element-8647">
              <span data-id="element-8648">Only 2 💗 per image</span>
              <span className="ml-auto text-of-blue text-xs" data-id="element-8649">Try now →</span>
            </div>
          </div>
        </div>
        {showFullGenerator && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300" onClick={handleBackdropClick} data-id="element-8650">
            <ImageGenerationUpsell modal onClose={() => setShowFullGenerator(false)} data-id="element-8651" />
          </div>}
      </div>;
  }
  // For the inline banner version
  if (inline) {
    return <div className="p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm" data-id="element-8652">
        <div className="flex items-center gap-2" data-id="element-8653">
          <div className="w-10 h-10 flex items-center justify-center bg-of-blue/10 dark:bg-of-blue/20 rounded-full text-of-blue" data-id="element-8654">
            <Camera size={20} data-id="element-8655" />
          </div>
          <div className="flex-1" data-id="element-8656">
            <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200" data-id="element-8657">
              Generate Custom Images
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1" data-id="element-8658">
              Create stunning AI images of your favorite angel for just 2 💗
              each
            </p>
          </div>
          <button className="px-3 py-1.5 bg-of-blue rounded-full text-white text-xs font-medium" onClick={() => setShowFullGenerator(true)} data-id="element-8659">
            Generate Now
          </button>
        </div>
        {showFullGenerator && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300" onClick={handleBackdropClick} data-id="element-8660">
            <ImageGenerationUpsell modal onClose={() => setShowFullGenerator(false)} data-id="element-8661" />
          </div>}
      </div>;
  }
  // Full modal version
  if (modal) {
    return <div className="w-full max-w-3xl bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-in zoom-in-90 duration-300 shadow-lg" onClick={e => e.stopPropagation()} // Prevent clicks from bubbling up to the backdrop
    data-id="element-8662">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center" data-id="element-8663">
          <h3 className="font-bold flex items-center gap-2 text-gray-800 dark:text-white" data-id="element-8664">
            <Wand2 size={18} className="text-of-blue" data-id="element-8665" />
            Generate Custom Images
          </h3>
          {onClose && <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400" data-id="element-8666">
              <X size={18} data-id="element-8667" />
            </button>}
        </div>
        <div className="p-4" data-id="element-8668">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-8669">
            <div className="relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700" data-id="element-8670">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/fHqa8oRhbQXV5S5MdTFfFy/image.png" alt="Angel preview" className="w-full h-48 object-cover" data-id="element-8671" />
              <div className="absolute top-2 left-2 px-2 py-1 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-full text-xs text-gray-700 dark:text-gray-300" data-id="element-8672">
                Zoe Harper
              </div>
              <button className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-of-blue/80 backdrop-blur-sm rounded-full" data-id="element-8673">
                <Camera size={14} className="text-white" data-id="element-8674" />
              </button>
            </div>
            <div className="space-y-3" data-id="element-8675">
              <div data-id="element-8676">
                <h4 className="text-xs font-medium mb-1 text-gray-700 dark:text-gray-300" data-id="element-8677">
                  Image settings
                </h4>
                <div className="p-2 bg-gray-50 dark:bg-gray-900 rounded-lg space-y-2 border border-gray-200 dark:border-gray-700" data-id="element-8678">
                  <div data-id="element-8679">
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-8680">
                      Style
                    </label>
                    <select className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-1.5 text-xs text-gray-700 dark:text-gray-300" data-id="element-8681">
                      <option data-id="element-8682">Natural</option>
                      <option data-id="element-8683">Glamour</option>
                      <option data-id="element-8684">Casual</option>
                      <option data-id="element-8685">Artistic</option>
                    </select>
                  </div>
                  <div data-id="element-8686">
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-8687">
                      Setting
                    </label>
                    <select className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-1.5 text-xs text-gray-700 dark:text-gray-300" data-id="element-8688">
                      <option data-id="element-8689">Indoor</option>
                      <option data-id="element-8690">Outdoor</option>
                      <option data-id="element-8691">Beach</option>
                      <option data-id="element-8692">Studio</option>
                    </select>
                  </div>
                </div>
              </div>
              <div data-id="element-8693">
                <h4 className="text-xs font-medium mb-1 text-gray-700 dark:text-gray-300" data-id="element-8694">
                  Number of images
                </h4>
                <div className="grid grid-cols-4 gap-1" data-id="element-8695">
                  {countOptions.map(count => <button key={count} className={`p-1.5 rounded-lg border ${selectedCount === count ? 'border-of-blue bg-of-blue/10 text-of-blue' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-of-blue/50'}`} onClick={() => setSelectedCount(count)} data-id="element-8696">
                      {count}
                    </button>)}
                </div>
              </div>
              <div className="flex items-center justify-between p-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800/30" data-id="element-8697">
                <div className="flex items-center gap-1" data-id="element-8698">
                  <Sparkles size={12} className="text-yellow-500" data-id="element-8699" />
                  <span className="text-xs text-gray-700 dark:text-gray-300" data-id="element-8700">
                    First-time users get 1 free image!
                  </span>
                </div>
                <span className="text-xs text-of-blue" data-id="element-8701">Limited offer</span>
              </div>
              <button className="w-full py-2 bg-of-blue rounded-lg text-white text-xs font-medium flex items-center justify-center gap-2 hover:bg-of-blue-dark transition-colors" onClick={handleGenerate} data-id="element-8702">
                <Sparkles size={14} data-id="element-8703" />
                Generate Image
                <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs" data-id="element-8704">
                  {selectedCount * 2} 💗
                </span>
              </button>
            </div>
          </div>
          {/* Suggestions Section */}
          <div className="mt-4" data-id="element-8705">
            <h4 className="text-sm font-medium mb-2 text-gray-700 dark:text-white" data-id="element-8706">
              Suggestions
            </h4>
            {/* Suggestion Tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 mb-3 overflow-x-auto" data-id="element-8707">
              {suggestionTabs.map(tab => <button key={tab} className={`px-3 py-1.5 text-xs font-medium whitespace-nowrap ${activeTab === tab ? 'text-of-blue border-b-2 border-of-blue' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} onClick={() => setActiveTab(tab)} data-id="element-8708">
                  {tab}
                </button>)}
            </div>
            {/* Suggestion Grid */}
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-id="element-8709">
              {poseSuggestions.map(suggestion => <div key={suggestion.id} className={`flex flex-col items-center cursor-pointer ${selectedSuggestion === suggestion.id ? 'scale-105 transition-transform' : 'opacity-80 hover:opacity-100'}`} onClick={() => handleSuggestionSelect(suggestion.id)} data-id="element-8710">
                  <div className={`w-full aspect-square rounded-lg overflow-hidden mb-1 ${selectedSuggestion === suggestion.id ? 'ring-2 ring-blue-500 ring-offset-1 ring-offset-gray-900' : 'border border-gray-700'}`} data-id="element-8711">
                    <img src={suggestion.image} alt={suggestion.label} className="w-full h-full object-cover" data-id="element-8712" />
                  </div>
                  <span className="text-xs text-gray-400 mt-0.5" data-id="element-8713">
                    {suggestion.label}
                  </span>
                </div>)}
            </div>
          </div>
          <div className="mt-4" data-id="element-8714">
            <h4 className="text-xs font-medium mb-1 text-gray-700 dark:text-gray-300" data-id="element-8715">
              Generated Images
            </h4>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-center border border-gray-200 dark:border-gray-700" data-id="element-8716">
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-8717">
                Your generated images will appear here. You can save them to
                your gallery or share with angels.
              </p>
            </div>
          </div>
        </div>
      </div>;
  }
  // Default small card version
  return <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 shadow-sm" data-id="element-8718">
      <div className="flex items-center justify-between mb-2" data-id="element-8719">
        <h3 className="font-medium flex items-center gap-1 text-gray-800 dark:text-gray-200" data-id="element-8720">
          <Camera size={16} className="text-of-blue" data-id="element-8721" />
          <span data-id="element-8722">Image Generator</span>
        </h3>
        <span className="text-xs px-2 py-0.5 bg-of-blue/10 dark:bg-of-blue/20 rounded-full text-of-blue" data-id="element-8723">
          NEW
        </span>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-8724">
        Generate custom images of your favorite angels in any style
      </p>
      <div className="flex justify-between items-center" data-id="element-8725">
        <div className="text-sm text-gray-700 dark:text-gray-300" data-id="element-8726">
          <span className="text-of-blue font-bold" data-id="element-8727">2 💗</span> per image
        </div>
        <button className="px-3 py-1.5 bg-of-blue rounded-full text-white text-xs font-medium flex items-center gap-1 hover:bg-of-blue-dark transition-colors" onClick={() => setShowFullGenerator(true)} data-id="element-8728">
          <Plus size={12} data-id="element-8729" />
          <span data-id="element-8730">Generate</span>
        </button>
      </div>
      {showFullGenerator && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300" onClick={handleBackdropClick} data-id="element-8731">
          <ImageGenerationUpsell modal onClose={() => setShowFullGenerator(false)} data-id="element-8732" />
        </div>}
    </div>;
};